import { useQuery } from "@tanstack/react-query";

import { getProjectRepository } from "../../data/projectRepository";
import "./projects.css";
import "./resource-room-projects.css";

interface Stage {
  title: string;
  description: string;
  href: string;
  action: string;
  done: boolean;
  fact: string;
}

export function ResourceRoomProjectHome({ projectId }: { projectId: string }): React.JSX.Element {
  const project = useQuery({
    queryKey: ["resource-room-project", projectId],
    queryFn: async () => (await getProjectRepository()).read(projectId)
  });
  if (project.isLoading) return <main className="page-shell"><p className="state-message">正在加载学期项目…</p></main>;
  if (project.isError || !project.data) {
    return (
      <main className="page-shell">
        <a className="back-link" href="/projects">← 返回全部项目</a>
        <p className="error-message">项目不存在或暂时无法读取。</p>
      </main>
    );
  }

  const data = project.data;
  const counts = data.module_counts;
  const importReady =
    (counts.students ?? 0) > 0 &&
    (counts.time_slots ?? 0) > 0 &&
    (counts.incomplete_students ?? 0) === 0;
  const natureReady = importReady && (counts.original_courses ?? 0) > 0 && (counts.unset_original_courses ?? 0) === 0;
  const schoolDataReady = natureReady;
  const resourceReady =
    schoolDataReady &&
    (counts.intervention_courses ?? 0) > 0 &&
    (counts.teachers ?? 0) > 0 &&
    (counts.rooms ?? 0) > 0;
  const planReady =
    resourceReady &&
    (counts.plans ?? 0) > 0 &&
    (counts.scheduled_plans ?? 0) === (counts.plans ?? 0);
  const stages: Stage[] = [
    {
      title: "学校基础数据",
      description: "依次完成原班课表、学生与班级、编辑节次和全校课程性质。",
      href: `/projects/${projectId}/student-timetables`,
      action: schoolDataReady ? "查看学校基础数据" : "完善学校基础数据",
      done: schoolDataReady,
      fact: schoolDataReady
        ? `${counts.students} 名学生 · ${counts.time_slots} 个节次 · ${counts.original_courses} 门课程`
        : (counts.incomplete_students ?? 0) > 0
          ? `${counts.incomplete_students} 名学生待补全年级班级`
          : importReady && (counts.unset_original_courses ?? 0) > 0
            ? `${counts.unset_original_courses} 门课程待设置性质`
          : "尚未导入"
    },
    {
      title: "资源资料",
      description: "维护干预课程、教师、教室和不可排时间。",
      href: `/projects/${projectId}/resources`,
      action: "准备资源资料",
      done: resourceReady,
      fact: !schoolDataReady ? "等待学校基础数据" : `${counts.intervention_courses ?? 0} 门课程 · ${counts.teachers ?? 0} 位教师 · ${counts.rooms ?? 0} 间教室`
    },
    {
      title: "干预排课",
      description: "建立个训或小组计划，一键排课或逐项选择时段。",
      href: `/projects/${projectId}/intervention-plans`,
      action: "进入干预排课",
      done: planReady,
      fact: !resourceReady
        ? "等待资源资料"
        : `${counts.plans ?? 0} 项计划 · ${counts.scheduled_plans ?? 0} 项已排${data.pending_plan_count ? ` · ${data.pending_plan_count} 项待排` : ""}${data.needs_confirmation_count ? ` · ${data.needs_confirmation_count} 项需确认` : ""}`
    },
    {
      title: "课表与导出",
      description: "查看资源教室、教师、学生和班级单双周课表。",
      href: `/projects/${projectId}/timetables`,
      action: "查看课表与导出",
      done: data.has_timetable,
      fact: data.has_timetable ? "已有可查看课表" : "尚无已排课计划"
    }
  ];
  const nextStage = stages.find((stage) => !stage.done) ?? stages[3];

  return (
    <main className="page-shell project-home resource-project-home">
      <header className="page-header">
        <div>
          <a className="back-link" href="/projects">← 全部学期项目</a>
          <p className="eyebrow">项目总览</p>
          <h1>{data.name}</h1>
        </div>
      </header>

      <section className="resource-next-card" aria-labelledby="resource-next-title">
        <div>
          <p className="focus-kicker">当前下一步</p>
          <h2 id="resource-next-title">{nextStage.title}</h2>
          <p>{nextStage.description}</p>
          {data.needs_confirmation_count > 0 ? (
            <p className="resource-warning">有 {data.needs_confirmation_count} 项计划需要重新确认，确认前不会进入课表与导出。</p>
          ) : null}
        </div>
        <a className="solid-button" href={nextStage.href}>{nextStage.action}</a>
      </section>

      <section className="resource-stage-list" aria-labelledby="resource-stage-title">
        <div className="resource-stage-heading">
          <p className="section-kicker">工作流程</p>
          <h2 id="resource-stage-title">四阶段进度</h2>
        </div>
        <ol>
          {stages.map((stage, index) => (
            <li className={stage.done ? "is-done" : stage === nextStage ? "is-current" : ""} key={stage.title}>
              <span className="resource-stage-number" aria-hidden="true">{stage.done ? "✓" : index + 1}</span>
              <div className="resource-stage-copy">
                <h3>{stage.title}</h3>
              </div>
              <span className="resource-stage-fact">{stage.fact}</span>
              <a className="mini-button" href={stage.href}>{stage.done ? "查看" : stage === nextStage ? "继续" : "打开"}</a>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
